import { SidebarInset, SidebarProvider } from "@myela/ui";
import { PartnerSidebar as AppSidebar } from "./PartnerSidebar";
import { PartnerHeader as AppHeader } from "./PartnerHeader";
import { BreadcrumbProvider } from "@/components/layout/BreadcrumbContext";
import ChatPanel, { ChatExpandCtx } from "./PartnerChatPanel";
import { useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function PartnerLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  // Myela chat visibility — mirrors the API's MYELA_ENABLED. Build-time flag
  // (VITE_*), default OFF; set VITE_MYELA_ENABLED=true to enable.
  const showChat =
    import.meta.env.VITE_MYELA_ENABLED === "true" &&
    user?.userType !== "GODADDY_MERCHANT";

  const [chatExpanded, setChatExpanded] = useState(false);
  const [collapsing, setCollapsing] = useState(false);

  // AppLayout is rendered per-page, so navigating remounts SidebarProvider and
  // its internal open state — a collapsed sidebar sprang back open on every
  // route change. Persist the desktop rail preference across remounts.
  const [sidebarDefaultOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("sidebar-state") !== "collapsed";
  });
  const handleSidebarOpenChange = useCallback((open: boolean) => {
    // Only remember the desktop state — the mobile Sheet always starts closed.
    if (window.matchMedia("(min-width: 1024px)").matches) {
      localStorage.setItem("sidebar-state", open ? "open" : "collapsed");
    }
  }, []);

  const collapse = useCallback(() => {
    if (!chatExpanded) return;
    setChatExpanded(false);
    setCollapsing(true);
    setTimeout(() => setCollapsing(false), 550);
  }, [chatExpanded]);

  return (
    <ChatExpandCtx.Provider
      value={{ expanded: chatExpanded, setExpanded: setChatExpanded, collapse }}
    >
      <SidebarProvider
        defaultOpen={sidebarDefaultOpen}
        onOpenChange={handleSidebarOpenChange}
        className="!h-svh !min-h-0 overflow-hidden bg-background text-foreground"
      >
        <AppSidebar />
        {/* Flush content area — solid full-height panel beside the sidebar (no inset/rounding). */}
        <SidebarInset className="!min-h-0 h-full flex flex-col overflow-hidden bg-background">
          <BreadcrumbProvider>
            <AppHeader />
            <div className="flex-1 overflow-y-auto relative [overflow-anchor:none]">
              {children}
              {showChat && (
                <ChatPanel variant="floating" collapsing={collapsing} />
              )}
            </div>
          </BreadcrumbProvider>
        </SidebarInset>
      </SidebarProvider>
    </ChatExpandCtx.Provider>
  );
}
